'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  alt: string;
  category: string;
  isVeg: boolean;
  popularity: number;
  isCurrentOffer?: boolean;
}

interface MenuItemCardProps {
  item: MenuItem;
  quantity: number;
  onQuantityChange: (itemId: string, quantity: number) => void;
  onAddToCart: (itemId: string) => void;
}

const MenuItemCard = ({ item, quantity, onQuantityChange, onAddToCart }: MenuItemCardProps) => {
  const handleIncrement = () => {
    onQuantityChange(item.id, quantity + 1);
  };

  const handleDecrement = () => {
    if (quantity > 0) {
      onQuantityChange(item.id, quantity - 1);
    }
  };

  const handleAddToCart = () => {
    if (quantity > 0) {
      onAddToCart(item.id);
    }
  };

  const handleCardClick = () => {
    if (quantity === 0) {
      onQuantityChange(item.id, 1);
    }
  };

  return (
    <article
      className="group tilt-card rounded-lg bg-card border border-border shadow-sm transition-smooth hover:shadow-md overflow-hidden cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative h-48 overflow-hidden bg-muted">
        <AppImage
          src={item.image}
          alt={item.alt}
          className="w-full h-full object-cover transition-smooth group-hover:scale-105"
        />
        {/* Veg/Non-Veg Indicator */}
        <div className="absolute top-3 left-3">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded border-2 ${
              item.isVeg ? 'bg-success border-success' : 'bg-error border-error'
            }`}
          >
            <div className={`w-3 h-3 rounded-full ${item.isVeg ? 'bg-white' : 'bg-white'}`} />
          </div>
        </div>
        {/* Popularity Badge */}
        {item.popularity >= 4.5 && (
          <div className="float-soft absolute top-3 right-3 flex items-center space-x-1 bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium shadow-sm">
            <Icon name="FireIcon" size={14} />
            <span>Popular</span>
          </div>
        )}
        {/* Current Offer Badge */}
        {item.isCurrentOffer && (
          <div className="absolute bottom-3 left-3 flex items-center space-x-1 bg-amber-500 text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm">
            <Icon name="TagIcon" size={14} />
            <span>Current Offer</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-heading text-lg font-semibold text-foreground line-clamp-1">
          {item.name}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{item.description}</p>

        {/* Price and Actions */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-data text-xl font-bold text-primary">₹{item.price.toFixed(2)}</span>

          {quantity === 0 ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onQuantityChange(item.id, 1);
              }}
              className="flex items-center space-x-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground shadow-sm transition-smooth hover-lift active-press focus-ring"
            >
              <Icon name="PlusIcon" size={16} />
              <span>Add</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={handleDecrement}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring"
                aria-label="Decrease quantity"
              >
                <Icon name="MinusIcon" size={16} />
              </button>
              <span className="text-data w-8 text-center text-sm font-medium text-foreground">
                {quantity}
              </span>
              <button
                type="button"
                onClick={handleIncrement}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-smooth hover:bg-muted focus-ring"
                aria-label="Increase quantity"
              >
                <Icon name="PlusIcon" size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Add to Cart Button */}
        {quantity > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart();
            }}
            className="mt-3 w-full flex items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-smooth hover-lift active-press focus-ring"
          >
            <Icon name="ShoppingCartIcon" size={16} />
            <span>Add to Cart</span>
          </button>
        )}
      </div>
    </article>
  );
};

export default MenuItemCard;
